export type Item = Record<string, any>;
export interface Session { authenticated: boolean; setupRequired: boolean; expiresAt?: string; title?: string; csrfToken?: string; }
export interface Field { key: string; label: string; type?: string; required?: boolean; hint?: string; placeholder?: string; disabled?: boolean; options?: {value:string;label:string}[]; rows?: number; language?: string; }
export const NAV = [
 {id:'dashboard',title:'대시보드',icon:'grid',group:'워크스페이스'},
 {id:'servers',title:'서버',icon:'server',group:''},
 {id:'services',title:'서비스',icon:'cube',group:''},
 {id:'domains',title:'DNS · 도메인',icon:'globe',group:''},
 {id:'images',title:'이미지 관리',icon:'layers',group:'운영 도구'},
 {id:'templates',title:'템플릿',icon:'layout',group:''},
 {id:'macros',title:'매크로',icon:'terminal',group:''},
 {id:'schedules',title:'예약 작업',icon:'calendar',group:''},
 {id:'reports',title:'리포트',icon:'chart',group:'관리'},
 {id:'operations',title:'작업 기록',icon:'history',group:''},
 {id:'system',title:'시스템',icon:'settings',group:''}
];
export const DESCRIPTIONS: Record<string,string> = {
 dashboard:'서버부터 서비스까지, 인프라의 현재 상태를 한눈에 확인하세요.',
 services:'실행할 서버를 직접 선택하고 서비스의 배포와 운영을 관리하세요.',
 servers:'로컬 서버와 SSH 서버의 자원과 실행 환경을 확인하세요.',
 domains:'Cloudflare DNS 레코드와 서비스의 연결 주소를 관리하세요.',
 images:'선택한 서버의 Docker 이미지를 확인하고 저장 공간을 관리하세요.',
 templates:'자주 사용하는 서비스 구성을 재사용 가능한 템플릿으로 만드세요.',
 macros:'반복하는 운영 명령을 저장하고 필요한 서버에서 실행하세요.',
 schedules:'반복 작업의 실행 시간과 결과를 한곳에서 관리하세요.',
 reports:'서버 자원과 서비스 상태를 분석한 운영 리포트를 확인하세요.',
 operations:'작업의 진행 상황과 실행 결과를 시간 순서로 확인하세요.',
 system:'워크스페이스, 브랜딩, 메일과 Agent 설정을 관리하세요.'
};
export function items(value: any): Item[] { return Array.isArray(value) ? value : value?.items ?? value?.content ?? []; }
const serverChoiceCollator = new Intl.Collator('ko-KR', { numeric: true, sensitivity: 'base' });
export function serverChoices(servers: readonly Item[]): Item[] {
 return [...servers].sort((a,b) => Number(b['executionMode']==='local')-Number(a['executionMode']==='local')
  || serverChoiceCollator.compare(String(a['name'] || ''),String(b['name'] || ''))
  || String(a['id']).localeCompare(String(b['id'])));
}
const serverSortStorageKey = 'infra-server-sort';
const serverSortOptions = ['name-asc', 'name-desc', 'score-asc', 'score-desc', 'host-asc', 'host-desc'];
const serverListCollator = new Intl.Collator('ko', {numeric:true, sensitivity:'base'});
export function readServerSort(): string {
 try {
  const saved = localStorage.getItem(serverSortStorageKey);
  if (saved && serverSortOptions.includes(saved)) return saved;
 } catch { /* Keep the default when storage is unavailable. */ }
 return 'name-asc';
}
export function saveServerSort(value: string): void {
 try { localStorage.setItem(serverSortStorageKey, value); } catch { /* Storage can be disabled. */ }
}
export function compareServers(a: Item, b: Item, sort: string): number {
 const local = Number(b['executionMode']==='local') - Number(a['executionMode']==='local');
 if (local) return local;
 const [field, direction] = sort.split('-');
 let comparison: number;
 if (field === 'score') {
  const missingA = a['score'] == null, missingB = b['score'] == null;
  if (missingA !== missingB) return missingA ? 1 : -1;
  comparison = missingA ? 0 : Number(a['score']) - Number(b['score']);
 } else comparison = serverListCollator.compare(String(a[field] || ''), String(b[field] || ''));
 return comparison * (direction === 'desc' ? -1 : 1) ||
  serverListCollator.compare(String(a['name'] || ''), String(b['name'] || '')) ||
  String(a['id']).localeCompare(String(b['id']));
}
export function errorMessage(error: any): string {
 const data=error?.data;
 return data?.message || data?.detail || data?.error || (error?.status === 401 ? '세션이 만료되었습니다. 다시 로그인해 주세요.' : error?.message === 'Failed to fetch' ? '서버에 연결할 수 없습니다. 연결 상태를 확인해 주세요.' : error?.message || '요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.');
}
export function date(value?: string): string { if(!value)return '—'; const d=new Date(value); return Number.isNaN(d.getTime())?'—':new Intl.DateTimeFormat('ko-KR',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',timeZone:'Asia/Seoul'}).format(d); }
export function status(value?: string): string { const labels:Record<string,string>={connected:'연결됨',disconnecting:'연결 해제 중','disconnect-failed':'연결 해제 실패',revoked:'연결 해제됨',configuring: '자동 구성 중', 'setup-failed': '자동 구성 실패', unverified:'연결 확인 전',unreachable:'연결 실패',healthy:'정상',generated:'생성됨',analyzing:'분석 중',optimizing:'최적화 중','ai-failed':'분석 일부 실패','mail-failed':'메일 발송 실패',normal:'정상',warning:'주의',unhealthy:'상태 이상',none:'점검 미설정',deleted:'제거됨',exited:'종료됨',created:'생성됨',restarting:'재시작 중',preparing:'준비 중',available:'연결됨',online:'연결됨',ready:'준비됨',registered:'등록됨',unknown:'확인 전',unconfigured:'설정 필요',pending:'대기',queued:'대기',running:'진행 중',deploying:'배포 중',verifying:'확인 중',succeeded:'완료',completed:'완료',failed:'실패',error:'오류',degraded:'확인 필요',offline:'연결 안 됨',stale:'수집 지연',stopped:'중지',canceled:'취소',cancelled:'취소',draft:'초안',disabled:'비활성',enabled:'활성',not_checked:'확인 전',active:'활성'};return labels[(value||'').toLowerCase()]||value||'확인 전'; }
export function tone(value?: string): string {return /^(healthy|connected|available|online|ready|succeeded|completed|active|enabled|normal)$/i.test(value||'')?'success':/^(critical|failed|disconnect-failed|setup-failed|ai-failed|mail-failed|error|unhealthy|unreachable|offline)$/i.test(value||'')?'danger':/^(warning|degraded|stale|unverified)$/i.test(value||'')?'warning':/^(running|disconnecting|configuring|deploying|queued|verifying|pending|preparing|analyzing|optimizing)$/i.test(value||'')?'info':'neutral';}
export function resourceTone(value:any):string {if(value===null||value===undefined||!Number.isFinite(Number(value)))return 'neutral';return Number(value)>=90?'danger':Number(value)>=75?'warning':'success';}
export function percent(value: any): string {return value === null || value === undefined ? '—' : Number(value).toLocaleString('ko-KR',{maximumFractionDigits:1})+'%';}
export function bytes(value:any):string {if(value===null||value===undefined)return '—';let n=Number(value);const u=['B','KB','MB','GB','TB'];let i=0;while(n>=1024&&i<4){n/=1024;i++;}return n.toLocaleString('ko-KR',{maximumFractionDigits:1})+' '+u[i];}

export function sessionExpiry(value?:string, now=new Date()):string {
 if(!value)return '';const expires=new Date(value);if(Number.isNaN(expires.getTime()))return '';
 const day=new Intl.DateTimeFormat('sv-SE',{timeZone:'Asia/Seoul'});
 const sameDay=day.format(expires)===day.format(now);
 return new Intl.DateTimeFormat('ko-KR',{...(sameDay?{}:{year:'numeric',month:'2-digit',day:'2-digit'}),hour:'2-digit',minute:'2-digit',hour12:false,timeZone:'Asia/Seoul'}).format(expires);
}

export const REASONING_OPTIONS = [
 {value:'minimal',label:'최소 (minimal)'}, {value:'low',label:'낮음 (low)'},
 {value:'medium',label:'보통 (medium)'}, {value:'high',label:'높음 (high)'},
 {value:'xhigh',label:'매우 높음 (xhigh)'}, {value:'ultra',label:'최대 (ultra)'}
];
export function reasoningLabel(value?:string):string {
 return REASONING_OPTIONS.find(option=>option.value===value)?.label || value || '미설정';
}
export function agentAuthLabel(value?:string):string {
 return ({authenticated:'로그인 확인',login_required:'로그인 필요',unavailable:'설치 필요',error:'확인 실패'} as Record<string,string>)[value||''] || '확인 전';
}
